import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table
} from "reactstrap";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { AssignStore, RevalidateSession } from "../../../requests";
import { toast } from "react-toastify";

const extractData = (response) => {
  if (!response) return null;
  if (response.data !== undefined) {
    if (response.data && response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  }
  if (response.success && response.data !== undefined) {
    return response.data;
  }
  return response;
};

export default function CustomerSessionPlayground() {
  const role = useSelector((state) => state?.login?.role);

  // Default coordinates: Harmu Outlet coordinates (approx 2.4 KM away from customer 23.3456, 85.3123)
  const [lat, setLat] = useState("23.3456");
  const [lng, setLng] = useState("85.3123");
  
  // Selected mock cart items for revalidation test
  const [selectedItems, setSelectedItems] = useState({
    PIZZA001: true, // Margherita
    PIZZA002: false, // Pepperoni
    PIZZA003: false, // Farmhouse
  });

  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);

  // Wait for role to be hydrated from Redux before redirecting
  if (role === null || role === undefined) return null;
  if (role !== "superadmin") return <Navigate to="/dashboard" />;

  const handleAssignStore = async () => {
    setError(null);
    setValidationResult(null);
    setDiagnostics(null);
    setTimeline([]);

    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    if (isNaN(latitude) || latitude < -90 || latitude > 90) {
      setError("Please enter a valid latitude between -90 and 90.");
      return;
    }
    if (isNaN(longitude) || longitude < -180 || longitude > 180) {
      setError("Please enter a valid longitude between -180 and 180.");
      return;
    }

    try {
      setLoading(true);
      const steps = [];
      steps.push({ step: "Request", detail: `Coordinates: ${latitude}, ${longitude}`, status: "COMPLETED" });

      const startTime = performance.now();
      const resp = await AssignStore(latitude, longitude);
      const data = extractData(resp);
      const latency = (performance.now() - startTime).toFixed(1);

      steps.push({ step: "Location Engine", detail: "Computed nearest active open stores within radius", status: "COMPLETED" });

      if (data && data.success) {
        steps.push({ step: "Store Selection", detail: `Assigned: ${data.assignedStore.storeName} (${data.assignedStore.storeId})`, status: "COMPLETED" });
        steps.push({ step: "Session Creation", detail: `Assigned At: ${data.session.assignedAt}`, status: "COMPLETED" });
        steps.push({ step: "Response", detail: "Customer Session initialized successfully", status: "COMPLETED" });
        
        setSession({
          assignedStoreId: data.assignedStore.storeId,
          storeName: data.assignedStore.storeName,
          address: data.assignedStore.address,
          distanceKm: data.assignedStore.distanceKm,
          assignedAt: data.session.assignedAt,
          expiresAt: data.session.expiresAt,
          storeActive: true,
          storeOpen: true,
          deliveryRadius: data.assignedStore.distanceKm > 5 ? data.assignedStore.distanceKm : 5 // Fallback representation
        });
        setDiagnostics({
          ...data.diagnostics,
          executionTimeMs: latency
        });
        toast.success(`Store Assigned: ${data.assignedStore.storeName}`);
      } else {
        steps.push({ step: "Store Selection", detail: "No eligible stores found within delivery radius", status: "FAILED" });
        steps.push({ step: "Response", detail: "Assignment failed", status: "FAILED" });
        setError(data?.reason || "NO_STORE_AVAILABLE");
        toast.error("No eligible store available for these coordinates.");
      }
      setTimeline(steps);
    } catch (err) {
      console.error("[CustomerSessionPlayground] Assign store error:", err);
      setError(err?.response?.data?.error?.message || err?.message || "Failed to assign store");
      toast.error("Server error assigning store.");
    } finally {
      setLoading(false);
    }
  };

  const handleRevalidateStore = async () => {
    setError(null);
    setValidationResult(null);

    if (!session) {
      setError("No active session. Please assign a store first.");
      return;
    }

    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    if (isNaN(latitude) || isNaN(longitude)) {
      setError("Please input valid coordinates.");
      return;
    }

    // Build items payload from checklist
    const items = Object.keys(selectedItems)
      .filter(id => selectedItems[id])
      .map(id => ({
        productId: id,
        variantId: "standard",
        quantity: 1
      }));

    try {
      setLoading(true);
      const resp = await RevalidateSession(latitude, longitude, session.assignedStoreId, items);
      const data = extractData(resp);

      if (data && data.success) {
        setValidationResult({
          status: "SUCCESS",
          message: "Validation passed. Session is still active and valid.",
          results: data.validationResults
        });
        toast.success("Session Validated Successfully!");
      } else if (data && data.isReassigned) {
        setValidationResult({
          status: "REASSIGNED",
          message: `Validation failed. Session has been reassigned to ${data.newStore.storeName} (${data.newStore.storeId}).`,
          newStore: data.newStore,
          cartRevalidation: data.cartRevalidation
        });
        // Automatically update session state
        setSession({
          ...session,
          assignedStoreId: data.newStore.storeId,
          storeName: data.newStore.storeName,
          distanceKm: data.newStore.distanceKm,
          address: data.newStore.address
        });
        toast.warning(`Session Reassigned to ${data.newStore.storeName}`);
      } else {
        setValidationResult({
          status: "FAILED",
          message: "Validation failed. No alternative eligible stores found.",
          reason: data?.reason || "NO_STORE_AVAILABLE"
        });
        toast.error("Validation Failed: No store available.");
      }
    } catch (err) {
      console.error("[CustomerSessionPlayground] Revalidate error:", err);
      setError(err?.response?.data?.error?.message || err?.message || "Failed to validate session");
      toast.error("Error performing revalidation.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearSession = () => {
    setSession(null);
    setDiagnostics(null);
    setTimeline([]);
    setValidationResult(null);
    setError(null);
    toast.info("Session Cleared.");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Developer Tools"
          breadcrumbItem="Customer Session Playground"
        />

        <Row>
          {/* Inputs Section */}
          <Col lg={4}>
            <Card className="shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center mb-4 gap-2">
                  <i
                    className="mdi mdi-account-cog-outline text-primary"
                    style={{ fontSize: "1.5rem" }}
                  />
                  <h5 className="mb-0 fw-semibold text-dark">Session Inputs</h5>
                </div>

                <FormGroup className="mb-3">
                  <Label for="latInput" className="fw-semibold">Customer Latitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    id="latInput"
                    placeholder="e.g. 23.3456"
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label for="lngInput" className="fw-semibold">Customer Longitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    id="lngInput"
                    placeholder="e.g. 85.3123"
                    value={lng}
                    onChange={(e) => setLng(e.target.value)}
                  />
                </FormGroup>

                {/* Cart Items Mock Selector */}
                <div className="mb-4 p-3 bg-light rounded border border-light-2">
                  <h6 className="fw-semibold mb-2 small text-muted text-uppercase">Mock Cart Items</h6>
                  <FormGroup check className="mb-2">
                    <Label check className="small">
                      <Input
                        type="checkbox"
                        checked={selectedItems.PIZZA001}
                        onChange={(e) => setSelectedItems({ ...selectedItems, PIZZA001: e.target.checked })}
                      />{" "}
                      Margherita Pizza (PIZZA001)
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mb-2">
                    <Label check className="small">
                      <Input
                        type="checkbox"
                        checked={selectedItems.PIZZA002}
                        onChange={(e) => setSelectedItems({ ...selectedItems, PIZZA002: e.target.checked })}
                      />{" "}
                      Pepperoni Pizza (PIZZA002)
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mb-0">
                    <Label check className="small">
                      <Input
                        type="checkbox"
                        checked={selectedItems.PIZZA003}
                        onChange={(e) => setSelectedItems({ ...selectedItems, PIZZA003: e.target.checked })}
                      />{" "}
                      Farmhouse Pizza (PIZZA003)
                    </Label>
                  </FormGroup>
                </div>

                <div className="d-flex flex-column gap-2">
                  <Button
                    color="primary"
                    className="w-100 fw-semibold"
                    onClick={handleAssignStore}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : "Assign Store"}
                  </Button>
                  
                  <Button
                    color="info"
                    className="w-100 fw-semibold text-white"
                    onClick={handleRevalidateStore}
                    disabled={loading || !session}
                  >
                    Revalidate Session
                  </Button>
                  
                  <Button
                    color="secondary"
                    outline
                    className="w-100 fw-semibold"
                    onClick={handleClearSession}
                  >
                    Clear Session
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Outputs Section */}
          <Col lg={8}>
            {error && (
              <Alert color="danger" className="border-0 shadow-sm mb-4">
                <i className="mdi mdi-alert-circle-outline me-2" />
                <strong>Failure:</strong> {error}
              </Alert>
            )}

            {session ? (
              <div className="fade-in">
                {/* Active Session Status */}
                <Card className="shadow-sm border-0 mb-4" style={{ borderLeft: "4px solid #34c38f" }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0 fw-semibold text-dark">Active Session Details</h5>
                      <Badge color="success" className="px-3 py-1 fs-6">SESSION_ACTIVE</Badge>
                    </div>

                    <Table responsive borderless className="align-middle mb-0">
                      <tbody>
                        <tr>
                          <td style={{ width: "35%" }} className="text-muted small">Assigned Store</td>
                          <td className="fw-bold text-success">{session.storeName} ({session.assignedStoreId})</td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Distance</td>
                          <td className="fw-semibold">{Number(session.distanceKm).toFixed(2)} km</td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Operating Hours</td>
                          <td className="small text-dark">
                            Store Active: <Badge color="success" className="me-2">Yes</Badge>
                            Store Open: <Badge color="success">Yes</Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Session Initialized At</td>
                          <td className="small"><code>{new Date(session.assignedAt).toLocaleString()}</code></td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Session Expiry</td>
                          <td className="small text-danger"><code>{new Date(session.expiresAt).toLocaleString()}</code></td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>

                {/* Validation Results */}
                {validationResult && (
                  <Card
                    className="shadow-sm border-0 mb-4"
                    style={{
                      borderLeft: validationResult.status === "SUCCESS"
                        ? "4px solid #34c38f"
                        : "4px solid #f1b44c"
                    }}
                  >
                    <CardBody>
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">
                        <i className="mdi mdi-checkbox-marked-circle-outline me-2" />
                        Revalidation Results
                      </h6>

                      <Alert
                        color={validationResult.status === "SUCCESS" ? "success" : "warning"}
                        className="border-0 mb-3"
                      >
                        <i className="mdi mdi-information-outline me-2" />
                        {validationResult.message}
                      </Alert>

                      {validationResult.status === "REASSIGNED" && (
                        <div className="p-3 bg-light rounded">
                          <h6 className="fw-bold mb-2 small text-uppercase">Cart Revalidation Summary</h6>
                          <div className="mb-2">
                            <span className="small text-muted d-block">New Assigned Store:</span>
                            <span className="fw-semibold text-warning">{validationResult.newStore.storeName} ({validationResult.newStore.storeId})</span>
                          </div>
                          <div>
                            <span className="small text-muted d-block">Price / Stock Changes:</span>
                            {validationResult.cartRevalidation?.unavailableItems?.length > 0 ? (
                              <div className="text-danger small mb-1">
                                ❌ {validationResult.cartRevalidation.unavailableItems.length} products unavailable in new store menu.
                              </div>
                            ) : (
                              <div className="text-success small mb-1">
                                ✔ All products are available in the new store menu.
                              </div>
                            )}
                            {validationResult.cartRevalidation?.priceChangedItems?.length > 0 && (
                              <div className="text-muted small">
                                ℹ Variant prices updated for the new outlet. Customer confirmation required.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}

                {/* Diagnostics */}
                {diagnostics && (
                  <Card className="shadow-sm border-0 mb-4">
                    <CardBody>
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">
                        <i className="mdi mdi-console me-2" />
                        Diagnostics & Timeline Trace
                      </h6>
                      <Row className="mb-4">
                        <Col sm={4} className="text-center">
                          <div className="fw-bold text-primary fs-3">{diagnostics.storesChecked || 0}</div>
                          <small className="text-muted">Stores Checked</small>
                        </Col>
                        <Col sm={4} className="text-center">
                          <div className="fw-bold text-success fs-3">{diagnostics.storesQualified || 0}</div>
                          <small className="text-muted">Stores Qualified</small>
                        </Col>
                        <Col sm={4} className="text-center">
                          <div className="fw-bold text-danger fs-3">{diagnostics.storesRejected || 0}</div>
                          <small className="text-muted">Stores Rejected</small>
                        </Col>
                      </Row>

                      {/* Timeline Display */}
                      <h6 className="fw-semibold text-dark mb-3">Execution Timeline Timeline</h6>
                      <div className="d-flex flex-column gap-3 pl-2">
                        {timeline.map((step, idx) => (
                          <div key={idx} className="d-flex align-items-start gap-3">
                            <div className="d-flex flex-column align-items-center">
                              <Badge
                                color={step.status === "FAILED" ? "danger" : "primary"}
                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: "24px", height: "24px" }}
                              >
                                {idx + 1}
                              </Badge>
                              {idx < timeline.length - 1 && (
                                <div className="bg-light-3 mt-1" style={{ width: "2px", height: "20px", background: "#eff2f7" }}></div>
                              )}
                            </div>
                            <div>
                              <div className="fw-bold text-dark small">{step.step}</div>
                              <div className="text-muted small">{step.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="shadow-sm border-0 text-center py-5 border-dashed">
                <CardBody className="py-5">
                  <i
                    className="mdi mdi-account-convert text-muted mb-3"
                    style={{ fontSize: "3rem" }}
                  />
                  <h5 className="text-muted">Awaiting Session Initialization</h5>
                  <p className="text-muted small mx-auto" style={{ maxWidth: "450px" }}>
                    Provide the coordinates of the customer on the left pane and click "Assign Store" to trace and initialize the Customer Session details.
                  </p>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
